import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ChartProvider from '../../../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../../../charts/echarts/ColorProvider'
import Tooltip from '../../../../../charts/echarts/Tooltip'
import AxisX from '../../../../../charts/echarts/AxisX'
import AxisY from '../../../../../charts/echarts/AxisY'
import ComponentBar from '../../../../../charts/echarts/ComponentBar'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { buildPointTooltipFormatter } from './trendTooltip'
import { alignSeriesToSharedAxis } from './alignSeries'

// Vertical bar variant of the trend tool. Multi-series capable -- when
// `seriesList` carries more than one entry ECharts auto-groups bars
// per category (grouped bar chart), preserving the per-period view
// while letting the user compare measures side-by-side.
//
// Tooltip uses `trigger: 'axis'` (vs the old single-bar `trigger: 'item'`)
// so the user sees every series's value at the hovered period rather
// than just the bar under the cursor. Consistent with the line
// variant's tooltip behavior.
//
// Single-series usage continues to produce one bar per period; bar
// width auto-adjusts.

export default function TrendBarChart ({ seriesList, valueFormat, frequency, height = 340 }) {
  const { formatter: fmt } = useFormattingContext()

  const { categories, alignedSeries } = useMemo(
    () => alignSeriesToSharedAxis(seriesList, frequency),
    [seriesList, frequency]
  )

  const seriesValueFormats = useMemo(() => {
    const map = {}
    for (const s of alignedSeries) {
      if (!s) continue
      if (s.id) map[s.id] = s.valueFormat || valueFormat
      if (s.name) map[s.name] = s.valueFormat || valueFormat
    }
    return map
  }, [alignedSeries, valueFormat])

  const tooltipFormatter = useMemo(
    () => buildPointTooltipFormatter(fmt, valueFormat, frequency, seriesValueFormats),
    [fmt, valueFormat, frequency, seriesValueFormats]
  )

  // colorBy='data' was nice for the old single-series bar chart (gave
  // each period its own swatch) but breaks the legend/tooltip mental
  // model for multi-series. For multi-series stick to colorBy='series'
  // (one color per series, matching the line chart). For single
  // series keep the old per-data tinting.
  const colorBy = alignedSeries.length > 1 ? 'series' : 'data'

  return (
    <ChartColorProvider theme='default'>
      <ChartProvider height={height} loadingTemplate='axis' emptyTemplate='axis'>
        <Tooltip trigger='axis' formatter={tooltipFormatter} />
        <AxisX
          type='category'
          data={categories}
          format={null}
          axisLabel={{ fontSize: 10, hideOverlap: true }}
        />
        <AxisY
          type='value'
          format={valueFormat || null}
          axisLabel={{ fontSize: 10 }}
          splitLine={{ lineStyle: { color: 'var(--opal-border-default, #eee)' } }}
        />
        {alignedSeries.map(s => (
          <ComponentBar
            key={s.id}
            seriesId={s.id}
            name={s.name}
            data={s.data}
            colorBy={colorBy}
            barCategoryGap='30%'
            barMinHeight={1}
            label={{ show: false }}
          />
        ))}
      </ChartProvider>
    </ChartColorProvider>
  )
}

TrendBarChart.propTypes = {
  seriesList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    valueFormat: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      value: PropTypes.number
    }))
  })).isRequired,
  valueFormat: PropTypes.string,
  frequency: PropTypes.string,
  height: PropTypes.number
}
